import React, { useState, useEffect, useCallback } from 'react'
import theme from '../../config/theme'
import Layout from '../../layout/layout'
import Input from '../../common/input'
import Table from '../../common/table'
import Pagination from '../../common/pagination'
import ActionMenu from '../../common/actionMenu'
import SnackBar from '../../common/snackBar'
import { IoAddOutline, IoSearchOutline } from 'react-icons/io5'
import { UserManagementDto } from '../../dtos/userManagement.dto'
import { userApi } from '../../utils/apiClient'
import ModifyDialog from './modify.dialog'
import DeleteDialog from './delete.dialog'
import { useAuth } from '../../contexts/authContext'

const UserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20) // Match API default size
  
  // API data states
  const [users, setUsers] = useState<UserManagementDto[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Dialog states
  const [isModifyDialogOpen, setIsModifyDialogOpen] = useState({ open: false, userId: undefined, userEmail: undefined, mode: 'create' } as { open: boolean, userId?: number, userEmail?: string, mode: 'create' | 'edit' })
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState({ open: false, userId: undefined, userEmail: undefined } as { open: boolean, userId?: number, userEmail?: string })
  
  // Snackbar states
  const [showCreateSuccessSnackbar, setShowCreateSuccessSnackbar] = useState(false)
  const [showUpdateSuccessSnackbar, setShowUpdateSuccessSnackbar] = useState(false)
  const [showDeleteSuccessSnackbar, setShowDeleteSuccessSnackbar] = useState(false)

  // Fetch users from API
  const fetchUsers = useCallback(async (username?: string, page: number = 0) => {
    setIsLoading(true)
    setError(null)
    
    try {
      // Convert UI page (1-based) to API page (0-based)
      const apiPage = page - 1
      
      const response = await userApi.searchUsers(
        username?.trim() || undefined,
        apiPage,
        itemsPerPage
      )
      
      setUsers(response.data || [])
      setTotalItems(response.meta?.totalItems || 0)
      setTotalPages(response.meta?.totalPages || 0)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch users'
      setError(errorMessage)
      setUsers([])
      setTotalItems(0)
      setTotalPages(0)
    } finally {
      setIsLoading(false)
    }
  }, [itemsPerPage])

  // Search query state for debounced fetching
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  // Debounced search - update debouncedSearchQuery after user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
      // Reset to page 1 when search changes
      setCurrentPage(1)
    }, 500) // 500ms debounce

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  // Fetch users when page or debounced search changes
  useEffect(() => {
    fetchUsers(debouncedSearchQuery, currentPage)
  }, [currentPage, debouncedSearchQuery, fetchUsers])

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Handle add user
  const handleAddUser = () => {
    setIsModifyDialogOpen({ open: true, mode: 'create' })
  }

  // Handle edit user
  const handleEditUser = (userId: string) => {
    const user = users.find(u => u.id.toString() === userId)
    setIsModifyDialogOpen({ open: true, mode: 'edit', userId: parseInt(userId), userEmail: user?.email })
  }

  // Handle delete user
  const handleDeleteUser = (userId: string) => {
    const user = users.find(u => u.id.toString() === userId)
    setIsDeleteDialogOpen({ open: true, userId: parseInt(userId), userEmail: user?.email })
  }

  // Table headers
  const header = ['User Name', 'Email', 'Action']
  const headerAlignments: ('left' | 'right' | 'center')[] = ['left', 'left', 'center']

  // Render table row
  const renderRow = (item: UserManagementDto) => {
    return (
      <>
        {/* User Name */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className="text-sm font-medium"
            style={{ color: theme.color.gray500 }}
          >
            {item.username || item.userName || ''}
          </span>
        </td>
        {/* Email */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className="text-sm"
            style={{ color: theme.color.gray500 }}
          >
            {item.email}
          </span>
        </td>
        {/* Action */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center justify-center">
            <ActionMenu
              itemId={item.id.toString()}
              onEdit={handleEditUser}
              onDelete={handleDeleteUser}
              disableDelete={currentUser?.email === item.email}
            />
          </div>
        </td>
      </>
    )
  }

  return (
    <Layout>
      <div className="flex flex-col overflow-hidden">
        {/* Page Header */}
        <div className="flex justify-between items-center mb-6">
          <h1
            className="text-2xl font-medium"
            style={{ color: theme.color.gray500 }}
          >
            User Management
          </h1>
          <button
            onClick={handleAddUser}
            className="flex items-center px-4 py-2 rounded-lg text-white font-medium transition-colors hover:shadow-lg"
            style={{ backgroundColor: theme.brandColor.red }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
            }}
          >
            {React.createElement(IoAddOutline as any, {
              className: "w-5 h-5 mr-2"
            })}
            Add User
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative" style={{ width: '100%' }}>
            <Input
              type="text"
              placeholder="Search by user name"
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearchQuery(e.target.value)
                // Page reset will be handled by debounce effect
              }}
              className="!pl-12"
            />
            <div
              className="absolute left-4"
              style={{
                top: '50%',
                transform: 'translateY(-50%)',
                color: theme.color.gray200,
                pointerEvents: 'none'
              }}
            >
              {React.createElement(IoSearchOutline as any, {
                className: "w-5 h-5"
              })}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="mb-4 p-4 rounded-lg text-sm"
            style={{
              backgroundColor: `${theme.color.error}20`,
              borderColor: `${theme.color.error}50`,
              color: theme.color.error,
              border: '1px solid'
            }}
          >
            {error}
          </div>
        )}

        {/* Table Container */}
        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="max-h-[calc(100vh-295px)] flex flex-col relative">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: theme.brandColor.red }}></div>
              </div>
            )}
            
            {/* Table */}
            {!isLoading && (
              <Table
                data={users}
                className={`flex-1 ${totalPages > 1 ? 'pb-0' : ''}`}
                header={header}
                headerAlignments={headerAlignments}
                renderRow={renderRow}
              />
            )}

            {/* Pagination - Fixed at bottom */}
            {!isLoading && totalPages > 0 && (
              <div className="fixed bottom-0 left-64 right-0 mx-6 py-4 z-10">
                <div className="flex justify-center">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={itemsPerPage}
                    totalItems={totalItems}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modify Dialog */}
      <ModifyDialog
        open={isModifyDialogOpen.open}
        userId={isModifyDialogOpen.userId}
        userEmail={isModifyDialogOpen.userEmail}
        mode={isModifyDialogOpen.mode}
        onClose={() => {
          setIsModifyDialogOpen({ open: false, userId: undefined, userEmail: undefined, mode: isModifyDialogOpen.mode })
        }}
        onSuccess={() => {
          // Show success toast
          if (isModifyDialogOpen.mode === 'create') {
            setShowCreateSuccessSnackbar(true)
          } else {
            setShowUpdateSuccessSnackbar(true)
          }
          // Refresh user list after successful create/update
          fetchUsers(debouncedSearchQuery, currentPage)
        }}
      />

      {/* Delete Dialog */}
      {isDeleteDialogOpen.open && (
        <DeleteDialog
          open={isDeleteDialogOpen.open}
          userId={isDeleteDialogOpen.userId}
          userEmail={isDeleteDialogOpen.userEmail}
          onClose={() => {
            setIsDeleteDialogOpen({ open: false, userId: undefined, userEmail: undefined })
          }}
          onSuccess={() => {
            // Show success toast
            setShowDeleteSuccessSnackbar(true)
            // Refresh user list after successful deletion
            fetchUsers(debouncedSearchQuery, currentPage)
          }}
        />
      )}
      
      {/* Success SnackBars */}
      {showCreateSuccessSnackbar && (
        <SnackBar
          message="User created successfully"
          type="success"
          show={showCreateSuccessSnackbar}
          onClose={() => setShowCreateSuccessSnackbar(false)}
          position="top-right"
          duration={3000}
        />
      )}
      {showUpdateSuccessSnackbar && (
        <SnackBar
          message="User updated successfully"
          type="success"
          show={showUpdateSuccessSnackbar}
          onClose={() => setShowUpdateSuccessSnackbar(false)}
          position="top-right"
          duration={3000}
        />
      )}
      {showDeleteSuccessSnackbar && (
        <SnackBar
          message="User deleted successfully"
          type="success"
          show={showDeleteSuccessSnackbar}
          onClose={() => setShowDeleteSuccessSnackbar(false)}
          position="top-right"
          duration={3000}
        />
      )}
    </Layout>
  )
}

export default UserManagementPage

